import React, { Component, Fragment } from 'react'
import c from 'classnames'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import { dispatch } from 'src/store'
import {
  setShouldShowMobileCategory,
  setShouldScroll,
  useRequestDemoScrollBehavior,
  showForm,
} from 'src/actions'
import Page from 'src/components/page'
import Image from 'src/components/image'
import Hrule from 'src/components/hrule'
import Vrule from 'src/components/vrule'
import Link from 'src/components/link'
import PillButton from 'src/components/pill-button'
import heroImage from 'src/assets/home-hero.png'
import webFraudIcon from 'src/assets/web-fraud-icon.svg'
import mobileFraudIcon from 'src/assets/mobile-fraud-icon.svg'
import closeCategoryTab from 'src/assets/close-category-tab.svg'
import config from 'src/config'

let fraudAssets = {}
config.fraudCategories.forEach(category => {
  fraudAssets[category.id] = {
    gif: require(`src/assets/fraud/${category.id}.gif`),
    icon: require(`src/assets/fraud/${category.id}-nav-icon.svg`),
  }
})

export class FraudCategoryPage extends Component {
  static propTypes = {
    className: PropTypes.string,
    pageContext: PropTypes.object.isRequired,
    windowWidth: PropTypes.number.isRequired,
    shouldShowMobileCategory: PropTypes.bool.isRequired,
  }

  static defaultProps = {
    className: '',
  }

  get className() {
    const { className } = this.props
    return c('im-fraud-category-page', className)
  }

  componentDidMount = () => {
    dispatch(useRequestDemoScrollBehavior())
    dispatch(showForm())
  }

  render() {
    const { className } = this
    const { windowWidth, shouldShowMobileCategory } = this.props
    const { category, categories } = this.props.pageContext
    const attributionFraud = categories.find(c => c.id === 'attribution-fraud')
    const leadGenFraud = categories.find(c => c.id === 'lead-gen-fraud')
    const installAttributionFraud = categories.find(c => c.id === 'install-attribution-fraud')
    const installFraud = categories.find(c => c.id === 'install-fraud')
    const isDesktop = windowWidth > 900

    return (
      <Fragment>
        <Page pageContentClassName={className} title={category.pageTitle}>
          <section className="hero">
            <section className="copy">
              <h1>Performance fraud, unpacked</h1>
              <p className="as-h3">
                Performance fraud targets both web and mobile environments — but what is it? How does it work?
                What does it look like? Knowledge is power, so take a look at this animated gallery of web and
                app-based fraud techniques and gain control.
              </p>
            </section>
            <section className="graphic">
              <Image src={heroImage} alt="Hero Graphic" cover={false} />
            </section>
          </section>
          <Hrule className="hrule-1" />
          {isDesktop ? (
            <section className="categories-desktop">
              <section className="tab-nav">
                {categories.map(cat => (
                  <Link
                    onClick={() => dispatch(setShouldScroll(true))}
                    to={`/protect-monitor/performance-fraud/${cat.id}`}
                    key={cat.id}
                    className={c('tab-nav-item', cat.id === category.id && 'selected')}>
                    <div className="cat-label">{cat.label} fraud</div>
                    <div className="underline" />
                  </Link>
                ))}
              </section>
              <section className="tab">
                <section className="copy">
                  <h4 className="device">{category.device}</h4>
                  <h3 className="as-h2">{`${category.label} fraud`}</h3>
                  <p className="summary" dangerouslySetInnerHTML={{ __html: category.summary }} />
                </section>
                <section className="graphic">
                  <Image src={fraudAssets[category.id].gif} alt={`${category.label} fraud`} cover={false} />
                </section>
              </section>
            </section>
          ) : (
            <section className="categories-mobile">
              <section className="tab-nav">
                {categories.map(cat => (
                  <Link
                    key={cat.id}
                    onClick={() => {
                      dispatch(setShouldShowMobileCategory(true))
                      dispatch(setShouldScroll(true))
                    }}
                    to={`/protect-monitor/performance-fraud/${cat.id}`}
                    className="tab-nav-item">
                    <section className="graphic">
                      <Image src={fraudAssets[cat.id].icon} alt="Category Nav Graphic" cover={false} />
                    </section>
                    <section className="copy">
                      <div className="device">{cat.device}</div>
                      <h3 className="cat-label">{cat.label} fraud</h3>
                    </section>
                  </Link>
                ))}
              </section>
            </section>
          )}
          <Hrule />
          <section className="techniques">
            <h3 className="as-h2">Fraud techniques</h3>
            <section className="technique-lists">
              <section className="column web">
                <Image className="graphic" src={webFraudIcon} alt="Web Fraud Graphic" cover={false} />
                <h4>Web fraud</h4>
                <section className="technique-list attribution-fraud">
                  <h4 className="as-h3">{attributionFraud.label}</h4>
                  {attributionFraud.fraudTechniques.map(t => (
                    <Link key={t.id} to={`/protect-monitor/performance-fraud/${t.id}`}>
                      <PillButton>{t.label}</PillButton>
                    </Link>
                  ))}
                </section>
                <section className="technique-list lead-gen-fraud">
                  <h4 className="as-h3">{leadGenFraud.label}</h4>
                  {leadGenFraud.fraudTechniques.map(t => (
                    <Link key={t.id} to={`/protect-monitor/performance-fraud/${t.id}`}>
                      <PillButton>{t.label}</PillButton>
                    </Link>
                  ))}
                </section>
              </section>
              <Vrule />
              <section className="column mobile">
                <Image className="graphic" src={mobileFraudIcon} alt="Mobile Fraud Graphic" cover={false} />
                <h4>Mobile fraud</h4>
                <section className="technique-list install-attribution-fraud">
                  <h4 className="as-h3">{installAttributionFraud.label}</h4>
                  {installAttributionFraud.fraudTechniques.map(t => (
                    <Link key={t.id} to={`/protect-monitor/performance-fraud/${t.id}`}>
                      <PillButton>{t.label}</PillButton>
                    </Link>
                  ))}
                </section>
                <section className="technique-list install-fraud">
                  <h4 className="as-h3">{installFraud.label}</h4>
                  {installFraud.fraudTechniques.map(t => (
                    <Link key={t.id} to={`/protect-monitor/performance-fraud/${t.id}`}>
                      <PillButton>{t.label}</PillButton>
                    </Link>
                  ))}
                </section>
              </section>
            </section>
          </section>
        </Page>
        <section className={c('im-category-tab', !isDesktop && shouldShowMobileCategory && 'visible')}>
          <section className="category-tab-content">
            <Image
              className="close-btn"
              src={closeCategoryTab}
              alt="Close Category Tab"
              cover={false}
              onClick={() => dispatch(setShouldShowMobileCategory(false))}
            />
            <section className="graphic">
              <Image src={fraudAssets[category.id].gif} alt={`${category.label} fraud`} cover={false} />
            </section>
            <section className="copy">
              <h4 className="device">{category.device}</h4>
              <h3>{`${category.label} fraud`}</h3>
              <p className="summary" dangerouslySetInnerHTML={{ __html: category.summary }} />
            </section>
          </section>
        </section>
      </Fragment>
    )
  }
}

export default connect(state => ({
  windowWidth: state.windowWidth,
  shouldShowMobileCategory: state.shouldShowMobileCategory,
}))(FraudCategoryPage)
